import React from 'react';
import { Modal, Row, Col, Tooltip, Divider } from 'antd';
import Text from 'antd/lib/typography/Text';
import {
  formatWholeNumber,
  formatNumber2SigFig,
  formatCurrency2SigFig,
} from '../../../../core/utils/campaigns.js';

const labelStyle = {
  fontSize: '13px',
  color: '#8c8c8c',
};

const valueStyle = {
  fontSize: '13px',
  fontWeight: 600,
  color: '#262626',
};

const sectionTitleStyle = {
  fontSize: '14px',
  fontWeight: 600,
  color: '#434343',
  marginBottom: '12px',
  display: 'block',
};

const DetailRow = ({ label, value, tooltip }) => (
  <Row
    style={{
      padding: '8px 0',
      borderBottom: '1px solid #f5f5f5',
    }}
    type="flex"
    justify="space-between"
    align="middle"
  >
    <Col>
      {tooltip ? (
        <Tooltip title={tooltip}>
          <Text style={labelStyle}>{label}</Text>
        </Tooltip>
      ) : (
        <Text style={labelStyle}>{label}</Text>
      )}
    </Col>
    <Col>
      <Text style={valueStyle}>{value}</Text>
    </Col>
  </Row>
);

const PercentageContent = ({ pacingData, status, switchPacingYesterday }) => {
  let pace = pacingData?.pace || 0;
  let budgetSpent = pacingData?.budgetSpent || 0;
  let budgetSpentToday = pacingData?.budgetSpentToday || 0;
  let budgetLeft = pacingData?.budgetLeft || 0;
  let dailyAvgBudgetLeft = pacingData?.dailyAvgBudgetLeft || 0;
  let totalBudget = pacingData?.overallBudget || 0;
  let totalBudgetSpent = pacingData?.overallSpend || 0;

  if (switchPacingYesterday) {
    pace = pacingData?.paceYesterday || 0;
    dailyAvgBudgetLeft = pacingData?.dailyAvgBudgetYesterday || 0;
    budgetLeft = pacingData?.budgetLeftYesterday || 0;
    budgetSpentToday = pacingData?.budgetSpentYesterday || 0;
    budgetSpent = pacingData?.budgetYesterday || 0;
  }

  if (status === 'FINISHED') {
    dailyAvgBudgetLeft = 0;
    budgetLeft = (pacingData?.totalBudget || 0) - (pacingData?.budgetSpent || 0);
    budgetSpentToday = 0;
    budgetSpent = pacingData?.budgetSpent || 0;
  }

  const paceColor = getPaceColor(pace, true);

  return (
    <>
      <Text style={sectionTitleStyle}>Budget Pacing</Text>

      <DetailRow
        label="Pace"
        value={<span style={{ color: paceColor }}>{formatNumber2SigFig(pace)}%</span>}
        tooltip="Pacing for Percentage pricing model"
      />
      <DetailRow
        label="Remaining"
        value={formatCurrency2SigFig(budgetLeft)}
      />
      <DetailRow
        label="Expected Daily"
        value={formatCurrency2SigFig(dailyAvgBudgetLeft)}
        tooltip="Daily goal"
      />
      <DetailRow
        label={switchPacingYesterday ? 'Spent Yesterday' : 'Spent Today'}
        value={formatCurrency2SigFig(budgetSpentToday ?? 0)}
        tooltip="Amount spent within the day"
      />
      <DetailRow
        label="Budget Spent"
        value={formatCurrency2SigFig(budgetSpent)}
        tooltip="Total amount spent"
      />
      <DetailRow
        label="Total Budget"
        value={formatCurrency2SigFig(totalBudget)}
        tooltip="Total Budget for the whole duration of the campaign"
      />
      <DetailRow
        label="Total Budget Spent"
        value={formatCurrency2SigFig(totalBudgetSpent)}
        tooltip="Total amount spent from the start of the campaign"
      />
    </>
  );
};

const ImpressionContent = ({ pacingData, row, status, switchPacingYesterday, paceAdditionalInfo }) => {
  let pace = pacingData?.pace || 0;
  let impressionsLeft = pacingData?.impressionsLeft || 0;
  let dailyImpressionsGoal = pacingData?.dailyAvgImpressionsLeft || 0;
  let todaysImpressions = paceAdditionalInfo?.[0]?.impressionServedToday || 0;
  let impressionsSpent = paceAdditionalInfo?.[0]?.impressionsServed || 0;
  let overallImpressions = pacingData?.overallImpressions || 0;
  let overallDeliveredImpressions = pacingData?.overallDeliveredImressions || 0;

  if (switchPacingYesterday) {
    pace = pacingData?.paceYesterday || 0;
    impressionsLeft = pacingData?.impressionsLeftYesterday || 0;
    dailyImpressionsGoal = pacingData?.dailyAvgImpressionsLeftYesterday || 0;
    todaysImpressions = pacingData?.impressionsYesterday || 0;
    impressionsSpent = pacingData?.totalImpressionsYesterday || 0;
  }

  if (status === 'FINISHED') {
    impressionsSpent = row?.aggregateData?.overallData?.aggImpressions || 0;
    dailyImpressionsGoal = 0;
    pace = pacingData?.pace;
    todaysImpressions = 0;
    impressionsLeft = (pacingData?.impressionGoal || 0) - impressionsSpent;
  }

  const paceColor = getPaceColor(pace, false);

  return (
    <>
      <Text style={sectionTitleStyle}>Impression Pacing</Text>

      <DetailRow
        label="Pace"
        value={<span style={{ color: paceColor }}>{pace !== null ? formatNumber2SigFig(pace) : 0}%</span>}
        tooltip={`Projected Pace. (Impression from ${switchPacingYesterday ? 'Yesterday' : 'Today'} * Days Remaining) + Served Impressions / Impression Goal`}
      />
      <DetailRow
        label={impressionsLeft < 0 ? 'Overage' : 'Remaining'}
        value={formatWholeNumber(impressionsLeft)}
      />
      {status !== 'FINISHED' && (
        <DetailRow
          label="Daily Goal"
          value={dailyImpressionsGoal ? formatWholeNumber(dailyImpressionsGoal) : '0'}
          tooltip="(Impression Goal - Served Impressions) / Days Remaining"
        />
      )}
      {status !== 'FINISHED' && (
        <DetailRow
          label={switchPacingYesterday ? 'Yesterday' : 'Today'}
          value={todaysImpressions ? formatWholeNumber(todaysImpressions) : '0'}
          tooltip="Number of impressions served within the day"
        />
      )}
      <DetailRow
        label="Served Impressions"
        value={impressionsSpent ? formatWholeNumber(impressionsSpent) : '0'}
        tooltip="Number of impressions served at the start of the day"
      />
      <DetailRow
        label="Total Delivered"
        value={overallDeliveredImpressions ? formatWholeNumber(overallDeliveredImpressions) : '0'}
        tooltip="Number of impressions served from the start of the campaign"
      />
      <DetailRow
        label="Impression Goal"
        value={overallImpressions ? formatWholeNumber(overallImpressions) : '0'}
        tooltip="Total Impression goal for the whole duration of the campaign"
      />
    </>
  );
};

const PacingDetailModal = ({
  visible,
  onClose,
  data,
}) => {
  const { pacingData, row, status, isPercentage, switchPacingYesterday, paceAdditionalInfo } = data || {};

  return (
    <Modal
      title={`Pacing Details — ${row?.name || 'Campaign'}`}
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={480}
    >
      {isPercentage ? (
        <PercentageContent
          pacingData={pacingData}
          status={status}
          switchPacingYesterday={switchPacingYesterday}
        />
      ) : (
        <ImpressionContent
          pacingData={pacingData}
          row={row}
          status={status}
          switchPacingYesterday={switchPacingYesterday}
          paceAdditionalInfo={paceAdditionalInfo}
        />
      )}

      <Divider style={{ margin: '12px 0' }} />
      <Text style={sectionTitleStyle}>Schedule</Text>

      <DetailRow
        label="Days Left"
        value={pacingData?.daysLeft ?? '—'}
        tooltip="Days remaining based on campaign end date"
      />
      <DetailRow
        label="Schedule Days Left"
        value={pacingData?.daysLeftFromSchedule ?? '—'}
        tooltip="Days remaining based on DSP schedule"
      />
    </Modal>
  );
};

function getPaceColor(pace, isPercentage) {
  if (!pace) return '#ff4d4f';
  if (isPercentage) {
    if (pace <= 100 && pace >= 50) return '#52c41a';
    if (pace >= 0 && pace < 50) return '#faad14';
    return '#ff4d4f';
  }
  if (pace >= 95) return '#52c41a';
  if (pace >= 91 && pace <= 95) return '#faad14';
  return '#ff4d4f';
}

export default PacingDetailModal;
